import { clampBoxToPage, resolveDropTarget } from './placement.utils.ts';
import type { BrowserBox, CanvasPlacement, PdfBox } from './PlacementCanvas.types.ts';
import type {
	DragLanding,
	DragLandingInput,
	MaxResizeWidthInput,
	NearestFreeBoxInput,
	SettlePlacementsInput,
	SettlePlacementsResult,
} from './placementCollision.types.ts';

/** Below this, an intersection is float noise from a pt↔px round trip, not an overlap. */
export const OVERLAP_EPSILON_PT = 0.01;

/** Strict interior intersection: boxes sitting edge to edge do not overlap. */
export const boxesOverlap = (a: PdfBox, b: PdfBox): boolean =>
	a.x < b.x + b.width - OVERLAP_EPSILON_PT &&
	b.x < a.x + a.width - OVERLAP_EPSILON_PT &&
	a.y < b.y + b.height - OVERLAP_EPSILON_PT &&
	b.y < a.y + a.height - OVERLAP_EPSILON_PT;

export const samePosition = (a: PdfBox, b: PdfBox): boolean => a.x === b.x && a.y === b.y;

const overlapsAny = (candidate: PdfBox, obstacles: PdfBox[]): boolean =>
	obstacles.some((obstacle) => boxesOverlap(candidate, obstacle));

/**
 * The closest position to `box` that is on the page and overlaps nothing, or null.
 *
 * For axis-aligned boxes the optimum has each coordinate either unchanged or flush with a page
 * or obstacle edge, so trying every such pair is exact.
 */
export const findNearestFreeBox = ({
	box,
	obstacles,
	page,
}: NearestFreeBoxInput): PdfBox | null => {
	const xs = [box.x, 0, page.widthPt - box.width];
	const ys = [box.y, 0, page.heightPt - box.height];
	obstacles.forEach((obstacle) => {
		xs.push(obstacle.x - box.width, obstacle.x + obstacle.width);
		ys.push(obstacle.y - box.height, obstacle.y + obstacle.height);
	});

	let best: PdfBox | null = null;
	let bestDistance = Number.POSITIVE_INFINITY;
	xs.forEach((x) => {
		if (x < -OVERLAP_EPSILON_PT || x + box.width > page.widthPt + OVERLAP_EPSILON_PT) {
			return;
		}
		ys.forEach((y) => {
			if (y < -OVERLAP_EPSILON_PT || y + box.height > page.heightPt + OVERLAP_EPSILON_PT) {
				return;
			}
			const candidate = { ...box, x, y };
			const distance = (x - box.x) ** 2 + (y - box.y) ** 2;
			if (distance < bestDistance && !overlapsAny(candidate, obstacles)) {
				best = candidate;
				bestDistance = distance;
			}
		});
	});
	return best;
};

// Top of the page first (PDF y grows upward), then left to right.
const byReadingOrder = (
	[keyA, a]: [string, CanvasPlacement],
	[keyB, b]: [string, CanvasPlacement]
): number =>
	b.pdfBox.y + b.pdfBox.height - (a.pdfBox.y + a.pdfBox.height) ||
	a.pdfBox.x - b.pdfBox.x ||
	keyA.localeCompare(keyB);

/**
 * Pull boxes onto their page and move them apart until none overlap, in reading order per page.
 *
 * Fixed boxes settle first, then every other box, then the yielding ones, so a box just added
 * gives way rather than shoving an existing one. A box with no free spot stays put and is
 * reported; it still counts as an obstacle for the boxes after it.
 */
export const settlePlacements = ({
	placements,
	pageDims,
	fixedPlacements,
	yieldingKeys,
}: SettlePlacementsInput): SettlePlacementsResult => {
	const moves = new Map<string, CanvasPlacement>();
	const shiftedKeys = new Set<string>();
	const unresolvedKeys = new Set<string>();

	pageDims.forEach((page, pageNum) => {
		const settled = fixedPlacements
			.filter((placement) => placement.pageNum === pageNum)
			.map((placement) => placement.pdfBox);
		const onPage = Array.from(placements.entries())
			.filter(([, placement]) => placement.pageNum === pageNum)
			.sort(byReadingOrder);
		const ordered = [
			...onPage.filter(([key]) => !yieldingKeys.has(key)),
			...onPage.filter(([key]) => yieldingKeys.has(key)),
		];

		ordered.forEach(([key, placement]) => {
			const basis = clampBoxToPage(placement.pdfBox, page);
			let final = basis;
			if (overlapsAny(basis, settled)) {
				const free = findNearestFreeBox({ box: basis, obstacles: settled, page });
				if (free) {
					final = free;
					shiftedKeys.add(key);
				} else {
					unresolvedKeys.add(key);
				}
			}
			if (final.x !== placement.pdfBox.x || final.y !== placement.pdfBox.y) {
				moves.set(key, { pageNum, pdfBox: final });
			}
			settled.push(final);
		});
	});

	return { moves, shiftedKeys, unresolvedKeys };
};

/** Every key whose box overlaps another box, or a fixed one, on its page. */
export const findOverlappingKeys = (
	placements: Map<string, CanvasPlacement>,
	fixedPlacements: CanvasPlacement[]
): Set<string> => {
	const entries = Array.from(placements.entries());
	const overlapping = new Set<string>();
	entries.forEach(([key, placement], index) => {
		entries.slice(index + 1).forEach(([otherKey, other]) => {
			if (
				other.pageNum === placement.pageNum &&
				boxesOverlap(placement.pdfBox, other.pdfBox)
			) {
				overlapping.add(key);
				overlapping.add(otherKey);
			}
		});
		const hitsFixed = fixedPlacements.some(
			(fixed) =>
				fixed.pageNum === placement.pageNum && boxesOverlap(placement.pdfBox, fixed.pdfBox)
		);
		if (hitsFixed) {
			overlapping.add(key);
		}
	});
	return overlapping;
};

// With the ratio locked, react-rnd shifts both the left and top edges for the top and left handles.
const GROWS_LEFT = new Set(['left', 'top', 'topLeft', 'bottomLeft']);
const GROWS_UP = new Set(['top', 'left', 'topLeft', 'topRight']);

/**
 * The widest a ratio-locked box can grow from `direction` without crossing a neighbour or the
 * page edge, in CSS pixels. Never less than its current width.
 *
 * Works in the frame of the fixed corner, mirrored so the box always grows right and down: a
 * neighbour ahead on both axes caps the width at whichever of its near edges the box meets last.
 */
export const maxResizeWidth = ({
	box,
	direction,
	ratio,
	obstacles,
	bounds,
}: MaxResizeWidthInput): number => {
	const growsLeft = GROWS_LEFT.has(direction);
	const growsUp = GROWS_UP.has(direction);
	const anchorX = growsLeft ? box.left + box.width : box.left;
	const anchorY = growsUp ? box.top + box.height : box.top;
	const nearX = (o: BrowserBox) => (growsLeft ? anchorX - (o.left + o.width) : o.left - anchorX);
	const farX = (o: BrowserBox) => (growsLeft ? anchorX - o.left : o.left + o.width - anchorX);
	const nearY = (o: BrowserBox) => (growsUp ? anchorY - (o.top + o.height) : o.top - anchorY);
	const farY = (o: BrowserBox) => (growsUp ? anchorY - o.top : o.top + o.height - anchorY);

	let limit = Math.min(
		growsLeft ? anchorX : bounds.width - anchorX,
		(growsUp ? anchorY : bounds.height - anchorY) * ratio
	);
	obstacles.forEach((obstacle) => {
		if (farX(obstacle) <= OVERLAP_EPSILON_PT || farY(obstacle) <= OVERLAP_EPSILON_PT) {
			return;
		}
		const cap = Math.max(nearX(obstacle), nearY(obstacle) * ratio);
		// Below the current width means it already overlaps: let the box be resized out of it.
		if (cap < box.width - OVERLAP_EPSILON_PT) {
			return;
		}
		limit = Math.min(limit, cap);
	});
	return Math.max(box.width, limit);
};

/** Where a dragged box would land if released now, against the boxes on the page under it. */
export const resolveDragLanding = ({
	obstaclesOnPage,
	...drop
}: DragLandingInput): DragLanding | null => {
	const target = resolveDropTarget(drop);
	if (!target) {
		return null;
	}
	const page = drop.pages.find((candidate) => candidate.pageNum === target.pageNum)?.dims;
	const obstacles = obstaclesOnPage(target.pageNum);
	if (!overlapsAny(target.pdfBox, obstacles)) {
		return {
			pageNum: target.pageNum,
			dropped: target.pdfBox,
			landing: target.pdfBox,
			isBlocked: false,
		};
	}
	const landing = page ? findNearestFreeBox({ box: target.pdfBox, obstacles, page }) : null;
	return { pageNum: target.pageNum, dropped: target.pdfBox, landing, isBlocked: true };
};
